import { QueryResult } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useQueryParameters, useViewer } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Accordion,
	AddIcon,
	Button,
	FlexGap,
	Heading3,
	SegmentedControl,
	tokens,
} from '@treams/treams-ui';

import { GenericGoalsList, GoalsQuery } from '../../../goals/GoalsList';
import { useModalLink } from '../../../navigation/ModalNavigation';

const Container = styled.div`
	border: 1px solid ${tokens.border.subtle};
	border-radius: ${tokens.radius.md}px;
	background: ${tokens.surface.white};
	padding: ${tokens.padding.container}px;
`;

const Body = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${tokens.gap.list}px;
	padding-top: ${tokens.padding.control}px;
	border-top: 1px solid ${tokens.border.subtle};
`;

function GoalsList(props: { userID: string }) {
	const { modal } = useQueryParameters(['modal']);
	const { t } = useTranslation();

	const [tab, setTab] = useState<'active' | 'completed'>('active');
	const [makingEdits, setMakingEdits] = useState(false);

	const goals = graphql.useIndividualGoalsQuery({
		fetchPolicy: 'network-only',
		variables: {
			id: props.userID,
			status: tab === 'active' ? graphql.GoalStatus.Active : graphql.GoalStatus.Completed,
			last: 10,
		},
	});

	useEffect(() => {
		if (!modal) {
			if (makingEdits) {
				setMakingEdits(false);
				setTab('active');
				goals.refetch();
			}
		} else {
			setMakingEdits(true);
		}
	}, [modal]);

	return (
		<Accordion
			initialIsCollapsed={false}
			title={t('evaluation.submission.setGoals.existing.title', {
				count: goals.data?.user.goalsV2.totalCount ?? 0,
			})}
			borderless
			small
			noPadding
		>
			<Body>
				<SegmentedControl
					options={[
						{ title: t('evaluation.submission.setGoals.existing.active'), value: 'active' },
						{
							title: t('evaluation.submission.setGoals.existing.completed'),
							value: 'completed',
						},
					]}
					value={tab}
					onChange={(value) => setTab(value as 'active' | 'completed')}
				/>
				<GenericGoalsList
					query={goals as QueryResult<GoalsQuery>}
					path={'user.goalsV2'}
					empty={{
						title: t('goals.index.empty.title'),
						description: t('goals.index.empty.description'),
					}}
					modal
					compact
				/>
			</Body>
		</Accordion>
	);
}

export default function EvaluationSubmissionSetGoals(props: { userID: string }) {
	const { t } = useTranslation();
	const viewer = useViewer();

	const newGoalLink = useModalLink(
		viewer?.id === props.userID ? '/goals/new' : `/users/${props.userID}/goals/new`,
		true,
	);

	return (
		<Container>
			<FlexGap
				alignItems="center"
				justifyContent="space-between"
				gap={tokens.gap.group}
				mb={`${tokens.gap.group}px`}
			>
				<Heading3>{t('evaluation.submission.setGoals.heading')}</Heading3>
				<Button
					icon={<AddIcon />}
					title={t('goals.index.toolbar.new')}
					link={newGoalLink}
					type="secondary"
				/>
			</FlexGap>
			<GoalsList userID={props.userID} />
		</Container>
	);
}
